import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDateRange, DATE_RANGE_OPTIONS } from '../contexts/DateRangeContext';
import { VERSION_CONFIG } from '../versionConfig';
import WelcomeQuoteModal from './WelcomeQuoteModal';

interface LayoutProps {
  children: React.ReactNode;
}

const LogoIcon = ({ className = "h-8 w-8" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 2L91.6 26V74L50 98L8.4 74V26L50 2Z" fill="#136BCE" />
    <path d="M50 22L74 36L50 50L26 36Z" stroke="white" strokeWidth="8" strokeLinejoin="round" />
    <path d="M26 36V70L50 84V50" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M74 36V70" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === '/dashboard';
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfileViewOpen, setIsProfileViewOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const dateRangeRef = useRef<HTMLDivElement>(null);

  const { user, logout } = useAuth();
  const { range, setRangeByKey } = useDateRange();

  useEffect(() => {
    if (!isDateRangeOpen) return;
    const handler = (e: MouseEvent) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target as Node)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDateRangeOpen]);

  // Current app version and build information
  const APP_VERSION = VERSION_CONFIG.admin.appVersion;
  const BUILD_NUMBER = VERSION_CONFIG.admin.buildNumber;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleToggleProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProfileViewOpen(false);
    setIsProfileOpen(!isProfileOpen);
  };

  const handleLogoutClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProfileOpen(false);
    setIsLogoutConfirmOpen(true);
  };

  const cancelLogout = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLogoutConfirmOpen(false);
  };

  const confirmLogout = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await logout();
    setIsLogoutConfirmOpen(false);
    navigate('/login', { replace: true });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'bg-purple-50 text-purple-700 ring-purple-600/20';
      case 'manager': return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      default: return 'bg-slate-50 text-slate-600 ring-slate-500/20';
    }
  };

  const inspectionSublinks = [
    { to: '/pilot-run-reports', label: 'Pilot Run', icon: 'rocket_launch' },
    { to: '/inspection-report', label: 'Fabric Inspection', icon: 'texture' },
    { to: '/line-inspection-reports', label: 'Line Inspection', icon: 'precision_manufacturing' },
    { to: '/final-inspection-reports', label: 'Final Inspection', icon: 'verified' },
    { to: '/container-inspection-reports', label: 'Container Inspection', icon: 'inventory_2' },
  ];

  const isInspectionActive = location.pathname.includes('/pilot-run-report') ||
    location.pathname.includes('/inspection-report') ||
    location.pathname.includes('/line-inspection-reports') ||
    location.pathname.includes('/final-inspection-reports') ||
    location.pathname.includes('/container-inspection-reports');

  return (
    <div className="flex h-[100dvh] flex-col relative overflow-hidden bg-slate-50">
      {/* Restore Header Button (only when hidden) */}
      {isHeaderHidden && (
        <button
          onClick={() => setIsHeaderHidden(false)}
          className="fixed top-4 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:right-8 z-[100] flex h-10 w-10 items-center justify-center rounded-xl bg-[#136bce] shadow-[0_8px_30px_rgba(19,107,206,0.4)] border border-blue-400/30 text-white hover:bg-blue-600 active:scale-95 transition-all animate-in fade-in slide-in-from-top-4 duration-500 print:hidden"
          title="Show Header"
        >
          <span className="material-symbols-outlined text-[24px]">keyboard_double_arrow_down</span>
        </button>
      )}

      {/* Mobile Drawer Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm md:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <LogoIcon className="h-8 w-8" />
              <span className="text-lg font-bold text-[#136BCE]">QA MATE</span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-50 text-[#136bce]' : 'text-slate-600 hover:bg-slate-50'
                }`
              }
            >
              <span className="material-symbols-outlined text-[22px]">dashboard</span>
              Dashboard
            </NavLink>
            <NavLink
              to="/purchase-orders"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-50 text-[#136bce]' : 'text-slate-600 hover:bg-slate-50'
                }`
              }
            >
              <span className="material-symbols-outlined text-[22px]">receipt_long</span>
              Purchase Order
            </NavLink>

            <div className="py-2">
              <p className="px-4 mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Inspection Report</p>
              {inspectionSublinks.map((sub) => (
                <NavLink
                  key={sub.label}
                  to={sub.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive && location.pathname === sub.to ? 'text-[#136bce] bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <span className="material-symbols-outlined text-[20px]">{sub.icon}</span>
                  {sub.label}
                </NavLink>
              ))}
            </div>

            <NavLink
              to="/analytics"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-50 text-[#136bce]' : 'text-slate-600 hover:bg-slate-50'
                }`
              }
            >
              <span className="material-symbols-outlined text-[22px]">analytics</span>
              Analytics
            </NavLink>
          </nav>
        </div>
      </div>

      <header className={`flex-none relative z-50 flex flex-col bg-white shadow-sm transition-all duration-500 ease-in-out print:hidden ${isHeaderHidden ? '-translate-y-full opacity-0 max-h-0 overflow-hidden invisible' : 'translate-y-0 opacity-100 max-h-40 visible'}`}>
        <div className="flex h-16 items-center justify-between border-b border-slate-100 px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-[28px]">menu</span>
            </button>

            <Link to="/dashboard" className="flex items-center gap-3">
              <LogoIcon className="h-10 w-10" />
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight text-[#136BCE] leading-none text-nowrap text-stroke-sm">QA MATE</span>
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mt-0.5 text-nowrap">AI Textile Inspector</span>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {isDashboard && (
              <>
                <div ref={dateRangeRef} className="relative hidden sm:block">
                  <button
                    type="button"
                    onClick={() => setIsDateRangeOpen(v => !v)}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                    <span>{range.label}</span>
                    <span className={`material-symbols-outlined text-[18px] transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`}>expand_more</span>
                  </button>
                  {isDateRangeOpen && (
                    <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 z-50 overflow-hidden animate-in fade-in zoom-in duration-150">
                      {DATE_RANGE_OPTIONS.map(opt => {
                        const active = opt.key === range.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => { setRangeByKey(opt.key); setIsDateRangeOpen(false); }}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors ${active ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                          >
                            <span>{opt.label}</span>
                            {active && <span className="material-symbols-outlined text-[18px]">check</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="hidden sm:block h-6 w-px bg-slate-200"></div>
              </>
            )}

            {/* Styled Hide Header Button */}
            <button
              type="button"
              onClick={() => setIsHeaderHidden(true)}
              className="group flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 hover:border-blue-200 hover:bg-blue-50 hover:text-[#136bce] transition-all active:scale-95 shadow-sm"
              title="Hide Header"
            >
              <span className="material-symbols-outlined text-[20px] transition-transform group-hover:-translate-y-0.5">keyboard_double_arrow_up</span>
            </button>

            <div className="relative">
              {/* Premium Styled Profile Avatar */}
              <div
                onClick={handleToggleProfile}
                className="h-9 w-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#136bce] to-[#0e52a0] text-white text-xs font-bold shadow-[0_4px_12px_rgba(19,107,206,0.25)] ring-2 ring-white cursor-pointer hover:shadow-lg hover:scale-105 transition-all active:scale-95 z-50 relative select-none"
              >
                {user ? getInitials(user.name) : 'U'}
              </div>

              {isProfileOpen && (
                <div
                  className="absolute right-0 mt-3 origin-top-right rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5 z-50 animate-in fade-in zoom-in duration-200 overflow-hidden"
                  style={{ width: isProfileViewOpen ? '19rem' : '14rem' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!isProfileViewOpen ? (
                    <>
                      {/* Compact header */}
                      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                        <div className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#136bce] to-[#0e52a0] text-white text-xs font-bold shadow">
                          {user ? getInitials(user.name) : 'U'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || 'User'}</p>
                          <p className="text-xs text-slate-500 truncate">{user?.email || 'user@example.com'}</p>
                        </div>
                      </div>
                      {/* Menu items */}
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setIsProfileViewOpen(true)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-blue-50 hover:text-[#136bce] transition-colors"
                        >
                          <span className="material-symbols-outlined text-[20px]">account_circle</span>
                          Your Profile
                          <span className="material-symbols-outlined text-[16px] ml-auto text-slate-400">chevron_right</span>
                        </button>
                      </div>
                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          type="button"
                          onClick={handleLogoutClick}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[20px]">logout</span>
                          Logout
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Profile view header */}
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
                        <button
                          type="button"
                          onClick={() => setIsProfileViewOpen(false)}
                          className="flex items-center justify-center h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                        </button>
                        <p className="text-sm font-semibold text-slate-700">My Profile</p>
                      </div>

                      {/* Avatar + Name */}
                      <div className="flex flex-col items-center gap-2 pt-5 pb-4 px-4 bg-gradient-to-b from-blue-50/60 to-white border-b border-slate-100">
                        <div className="h-14 w-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#136bce] to-[#0e52a0] text-white text-xl font-bold shadow-lg shadow-blue-200/50">
                          {user ? getInitials(user.name) : 'U'}
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-slate-900">{user?.name || '—'}</p>
                          <p className="text-xs text-slate-500">{user?.email || '—'}</p>
                        </div>
                        {user?.role && (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${getRoleBadgeStyle(user.role)}`}>
                            {user.role}
                          </span>
                        )}
                      </div>

                      {/* Details */}
                      <div className="px-4 py-3 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-[18px] text-slate-400">verified_user</span>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 leading-none mb-0.5">Status</p>
                            <p className={`text-xs font-semibold ${user?.is_active ? 'text-emerald-600' : 'text-red-500'}`}>
                              {user?.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-[18px] text-slate-400">schedule</span>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 leading-none mb-0.5">Last Login</p>
                            <p className="text-xs text-slate-700 font-medium">{formatDate(user?.last_login)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-[18px] text-slate-400">calendar_add_on</span>
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 leading-none mb-0.5">Member Since</p>
                            <p className="text-xs text-slate-700 font-medium">{formatDate(user?.created_at)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Footer action */}
                      <div className="border-t border-slate-100 px-4 py-2.5">
                        <button
                          type="button"
                          onClick={handleLogoutClick}
                          className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[16px]">logout</span>
                          Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Navigation Menu: Full Width */}
        <div className="hidden md:block px-6 lg:px-10">
          <nav className="flex space-x-4 lg:space-x-8 overflow-visible">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `group flex items-center gap-2 border-b-[3px] py-3 text-sm transition-all whitespace-nowrap ${isActive
                  ? 'border-[#136bce] font-semibold text-[#136bce]'
                  : 'border-transparent font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">dashboard</span>
              Dashboard
            </NavLink>
            <NavLink
              to="/purchase-orders"
              className={({ isActive }) =>
                `group flex items-center gap-2 border-b-[3px] py-3 text-sm transition-all whitespace-nowrap ${isActive
                  ? 'border-[#136bce] font-semibold text-[#136bce]'
                  : 'border-transparent font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">receipt_long</span>
              Purchase Order
            </NavLink>

            <div className="relative group">
              <button type="button" className={`flex items-center gap-2 border-b-[3px] py-3 text-sm font-medium transition-all focus:outline-none whitespace-nowrap ${isInspectionActive
                ? 'border-[#136bce] text-[#136bce]'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}>
                <span className="material-symbols-outlined text-[20px]">fact_check</span>
                Inspection Report
                <span className="material-symbols-outlined text-[20px]">expand_more</span>
              </button>
              <div className="absolute left-0 top-full z-50 hidden w-64 pt-1 group-hover:block">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl ring-1 ring-black/5">
                  {inspectionSublinks.map((sub, i) => (
                    <NavLink
                      key={i}
                      to={sub.to}
                      className="group flex items-center gap-3 border-b border-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors last:border-0 hover:bg-slate-50 hover:text-[#136bce]"
                    >
                      <span className="material-symbols-outlined text-[18px] text-slate-400 group-hover:text-[#136bce] transition-colors">{sub.icon}</span>
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>

            <NavLink
              to="/analytics"
              className={({ isActive }) =>
                `group flex items-center gap-2 border-b-[3px] py-3 text-sm transition-all whitespace-nowrap ${isActive
                  ? 'border-[#136bce] font-semibold text-[#136bce]'
                  : 'border-transparent font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">analytics</span>
              Analytics
            </NavLink>
            <NavLink
              to="/master-data"
              className={({ isActive }) =>
                `group flex items-center gap-2 border-b-[3px] py-3 text-sm transition-all whitespace-nowrap ${isActive
                  ? 'border-[#136bce] font-semibold text-[#136bce]'
                  : 'border-transparent font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">storage</span>
              Master Data
            </NavLink>
            <NavLink
              to="/user-management"
              className={({ isActive }) =>
                `group flex items-center gap-2 border-b-[3px] py-3 text-sm transition-all whitespace-nowrap ${isActive
                  ? 'border-[#136bce] font-semibold text-[#136bce]'
                  : 'border-transparent font-medium text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">manage_accounts</span>
              User Management
            </NavLink>
          </nav>
        </div>
      </header>

      {/* Main Content: Scrollable */}
      <main className="flex-1 overflow-y-auto px-6 py-6 lg:px-10 custom-scrollbar relative">
        <div className="w-full">
          {children}
        </div>
      </main>

      {/* Footer: Full Width */}
      <footer className="flex-none border-t border-slate-200 bg-white px-6 py-6 lg:px-10 print:hidden">
        <div className="w-full">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">QA MATE Inspect Pro</span>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-[#136bce] ring-1 ring-inset ring-blue-700/10 uppercase tracking-tighter">
                  Enterprise
                </span>
              </div>
              <p className="text-[10px] text-slate-500">
                © {new Date().getFullYear()} QA MATE Systems. All rights reserved.
              </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
              <nav className="flex items-center gap-4 text-[10px] font-medium text-slate-500">
                <Link to="/privacy-policy" className="hover:text-[#136bce] transition-colors">Privacy Policy</Link>
                <span className="h-1 w-1 rounded-full bg-slate-300"></span>
                <Link to="/terms-conditions" className="hover:text-[#136bce] transition-colors">Terms & Conditions</Link>
              </nav>

              <div className="flex items-center gap-2 border-l border-slate-200 pl-4 md:pl-8">
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-1.5 text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                    <span className="material-symbols-outlined text-[12px]">terminal</span>
                    Build Info
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-medium text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                      {APP_VERSION}
                    </span>
                    <span className="text-[10px] font-mono font-medium text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 hidden sm:inline-block">
                      {BUILD_NUMBER}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals & Overlays */}
      {isProfileOpen && (
        <div
          className="fixed inset-0 z-40 bg-transparent"
          onClick={() => setIsProfileOpen(false)}
        />
      )}

      {isLogoutConfirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto print:hidden">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={cancelLogout}
          ></div>
          <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all border border-slate-100 text-center animate-in zoom-in-95 duration-200">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
              <span className="material-symbols-outlined text-[32px]">warning</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Logout of Account?</h3>
            <p className="text-slate-500 text-sm mb-8">
              Are you sure you want to logout? You will need to sign in again to access your dashboard and active sessions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                onClick={cancelLogout}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-red-600 px-4 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-md shadow-red-200 transition-all active:scale-[0.98]"
                onClick={confirmLogout}
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}

      <WelcomeQuoteModal />
    </div>
  );
};

export default Layout;